import React, { useRef, useState } from 'react';

import { Box, Image, List, ListItem, Text, VStack } from '@chakra-ui/react';
import { SignInModal, SignUpForm } from '~/components/organisms';
import { useUser } from '~/containers';
import { AuthState } from '@aws-amplify/ui-components';
import CampaignHeader from '~/components/templates/202208_campaign_header.jpg';
import LogoList from './LogoList';

import * as images from '../images';

import {
  SmartphoneBr,
  HeadingText,
  SubtitleText,
  HighlightedText,
  ParagraphText,
  Section,
} from './commonComponents';
import TopHeader from './TopHeader';
import ProblemSection from './ProblemSection';
import ReasonSection from './ReasonSection';
import SignUpButton from './SignUpButton';
import { TopPageVariant } from './types';
import FeatureTopics from './FeatureTopics';

const Top: React.FC<{ variant: TopPageVariant }> = (props) => {
  const { variant } = props;
  const { user, loading } = useUser();
  const [open, setOpen] = useState(false);
  const onClose = () => {
    setOpen(false);
  };
  const [defaultAuthState, setDefaultAuthState] = useState<
    AuthState.SignIn | AuthState.SignUp
  >(AuthState.SignUp);

  const formRef = useRef<HTMLDivElement>(null);
  const scrollToForm = () =>
    formRef.current?.scrollIntoView({ behavior: 'smooth' });

  return !loading && user ? (
    // ログインダイアログがフッター部分にかぶらないように height を確保
    <Box height={700} />
  ) : (
    <>
      <TopHeader variant={variant} scrollToForm={scrollToForm} />

      {variant === 'intern' && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 5 }}>
          <Image src={CampaignHeader} width="100%" alt="" />
        </Box>
      )}

      <ProblemSection />

      <Section bgColor="white" textAlign="center">
        <Text
          display="table"
          margin="auto"
          position="relative"
          bg="linear-gradient(to right, #02b9e4, #193195)"
          color="#ffe12e"
          borderRadius="20px"
          fontSize={{ base: 'lg', sm: '2xl' }}
          fontWeight="700"
          padding={{ base: '6px 12px', sm: '6px 32px' }}
          _after={{
            position: 'absolute',
            content: "''",
            top: '100%',
            left: 0,
            right: 0,
            margin: 'auto',
            border: '15px solid transparent',
            borderTop: '15px solid #0e75bd',
            width: 0,
          }}
        >
          そんなあなたにおすすめなのが
          <SmartphoneBr />
          リケイマッチ
        </Text>
        <HeadingText py="20px">リケイマッチとは</HeadingText>
        <ParagraphText fontSize="md">
          リケイマッチは
          <SmartphoneBr />
          「理系学部特化」の就活サイトで、
          <br />
          <HighlightedText bg="linear-gradient(transparent 80%, #ffe12e 0%)">
            プロフィールを登録するだけで
            <SmartphoneBr />
            企業からスカウトメールが受け取れる
          </HighlightedText>
          <SmartphoneBr />
          逆求人型の就活サイトです。
          <br />
          <br />
          すでに様々な就活サイトがありますが、
          <br />
          「一部の上位大学に特化」
          <SmartphoneBr />
          「理系大学院生に特化」
          <SmartphoneBr />
          等が目立っており、
          <br />
          「理系学部生ならではの就職活動の難しさ」
          <SmartphoneBr />
          に焦点を当てたものが存在しない事など
          <SmartphoneBr />
          踏まえて開発しました。
        </ParagraphText>
        <Box
          w="100%"
          display="flex"
          flexDirection="column"
          alignItems="center"
          paddingTop="30px"
          paddingBottom="50px"
          px="15px"
        >
          <Image src={images.connectCompanyWithStudent} width="640px" alt="" />
        </Box>
      </Section>

      <ReasonSection variant={variant} scrollToForm={scrollToForm} />

      <Section bgColor="white" textAlign="center">
        <HeadingText>
          ご利用の流れ
          <SubtitleText>flow</SubtitleText>
        </HeadingText>
        <ParagraphText paddingTop="40px" fontSize={{ base: 'md', sm: 'xl' }}>
          最小限のプロフィールを登録することから
          <SmartphoneBr />
          ご利用いただくことも可能です。
          <br />
          まずはお気軽にご登録ください。
        </ParagraphText>
        <List
          display="flex"
          justifyContent={{ base: 'center', sm: 'space-between' }}
          flexWrap={{ base: 'wrap', sm: 'nowrap' }}
          gap={{ base: '50px', sm: 'unset' }}
          maxW="95%"
          margin="46px auto 0"
        >
          <ListItem
            w="100%"
            maxW="240px"
            borderRadius="20px"
            paddingRight="20px"
            paddingBottom="20px"
            filter="drop-shadow(0 0 10px rgba(0,0,0,.05))"
            display="flex"
            flexDirection="column"
            alignItems="center"
          >
            <Image
              src={images.flow1}
              width="140px"
              alt=""
              paddingBottom="20px"
            />
            <ParagraphText
              color="#0a94cf"
              fontSize="1.1rem"
              paddingBottom="20px"
            >
              リケイマッチに
              <SmartphoneBr />
              登録
            </ParagraphText>
            <ParagraphText fontSize="md">
              まずは簡易的な情報から気軽に登録
            </ParagraphText>
          </ListItem>
          <ListItem
            width="52px"
            minW="42px"
            paddingTop="110px"
            paddingRight="30px"
            display={{ base: 'none', md: 'flex' }}
            flexDirection="column"
            alignItems="center"
          >
            <Image src={images.arrowRight} width="42px" alt="" />
          </ListItem>
          <ListItem
            w="100%"
            maxW="240px"
            borderRadius="20px"
            paddingRight="20px"
            paddingBottom="20px"
            filter="drop-shadow(0 0 10px rgba(0,0,0,.05))"
            display="flex"
            flexDirection="column"
            alignItems="center"
          >
            <Image
              src={images.flow2}
              width="140px"
              alt=""
              paddingBottom="20px"
            />
            <ParagraphText
              color="#0a94cf"
              fontSize="1.1rem"
              paddingBottom="20px"
            >
              プロフィールを
              <SmartphoneBr />
              入力
            </ParagraphText>
            <ParagraphText fontSize="md">
              学習内容、研究で取り組む予定の内容、就活で重視することなどあなたならではのプロフィールを作成
            </ParagraphText>
          </ListItem>
          <ListItem
            width="52px"
            minW="42px"
            paddingTop="110px"
            paddingRight="30px"
            display={{ base: 'none', md: 'flex' }}
            flexDirection="column"
            alignItems="center"
          >
            <Image src={images.arrowRight} width="42px" alt="" />
          </ListItem>
          <ListItem
            w="100%"
            maxW="240px"
            borderRadius="20px"
            paddingRight="20px"
            filter="drop-shadow(0 0 10px rgba(0,0,0,.05))"
            display="flex"
            flexDirection="column"
            alignItems="center"
          >
            <Image
              src={images.flow3}
              width="140px"
              alt=""
              paddingBottom="20px"
            />
            <ParagraphText
              color="#0a94cf"
              fontSize="1.1rem"
              paddingBottom="20px"
            >
              企業から
              <SmartphoneBr />
              オファーが届く
            </ParagraphText>
            <ParagraphText fontSize="md">
              あなたのプロフィールに興味を持った採用担当者から直接オファーが届く
            </ParagraphText>
          </ListItem>
          <ListItem
            width="52px"
            minW="42px"
            paddingTop="110px"
            paddingRight="30px"
            display={{ base: 'none', md: 'flex' }}
            flexDirection="column"
            alignItems="center"
          >
            <Image src={images.arrowRight} width="42px" alt="" />
          </ListItem>
          <ListItem
            w="100%"
            maxW="240px"
            borderRadius="20px"
            paddingRight="20px"
            filter="drop-shadow(0 0 10px rgba(0,0,0,.05))"
            display="flex"
            flexDirection="column"
            alignItems="center"
          >
            <Image
              src={images.flow4}
              width="140px"
              alt=""
              paddingBottom="20px"
            />
            <ParagraphText
              color="#0a94cf"
              fontSize="1.1rem"
              paddingBottom="20px"
            >
              内定が
              <SmartphoneBr />
              出る
            </ParagraphText>
            <ParagraphText fontSize="md">
              面談、選考を通して内定へ
            </ParagraphText>
          </ListItem>
        </List>
        <VStack w="100%" alignItems="center" paddingTop="30px">
          <SignUpButton variant={variant} onClick={() => scrollToForm()} />
        </VStack>
      </Section>

      <Section bg="radial-gradient(#02b9e4, #193195)" textAlign="center">
        <HeadingText color="white">
          登録企業の例
          <SubtitleText color="white">example</SubtitleText>
        </HeadingText>

        <LogoList />
        <List
          display="flex"
          justifyContent="center"
          flexWrap="wrap"
          maxW="1040px"
          margin={{ base: '30px auto 0', md: '45px auto 0' }}
        >
          <ListItem
            w="100%"
            maxW="240px"
            margin="10px"
            bgColor="white"
            borderRadius="20px"
            padding="20px"
            paddingBottom="40px"
            filter="drop-shadow(0 0 10px rgba(0,0,0,.05))"
            textAlign="left"
          >
            <p>A社</p>
            <ParagraphText fontSize="1.1rem" paddingBottom="10px" minH="60px">
              不動産Techのクラウドサービス開発
            </ParagraphText>
            <dl>
              <Box
                as="dt"
                borderBottom="1px solid #000d1f"
                color="#0a94cf"
                fontWeight="700"
              >
                募集
              </Box>
              <Box as="dd" paddingBottom="10px" minH="60px">
                Webエンジニア
              </Box>
              <Box
                as="dt"
                borderBottom="1px solid #000d1f"
                color="#0a94cf"
                fontWeight="700"
              >
                学べる技術
              </Box>
              <dd>PHP、MySQLなど</dd>
            </dl>
          </ListItem>
          <ListItem
            w="100%"
            maxW="240px"
            margin="10px"
            bgColor="white"
            borderRadius="20px"
            padding="20px"
            paddingBottom="40px"
            filter="drop-shadow(0 0 10px rgba(0,0,0,.05))"
            textAlign="left"
          >
            <p>B社</p>
            <ParagraphText fontSize="1.1rem" paddingBottom="10px" minH="60px">
              AI音声認識ツールの開発
            </ParagraphText>
            <dl>
              <Box
                as="dt"
                borderBottom="1px solid #000d1f"
                color="#0a94cf"
                fontWeight="700"
              >
                募集
              </Box>
              <Box as="dd" paddingBottom="10px" h="60px">
                Webエンジニア、機械学習エンジニア
              </Box>
              <Box
                as="dt"
                borderBottom="1px solid #000d1f"
                color="#0a94cf"
                fontWeight="700"
              >
                学べる技術
              </Box>
              <dd>Ruby、Pythonなど</dd>
            </dl>
          </ListItem>
          <ListItem
            w="100%"
            maxW="240px"
            margin="10px"
            bgColor="white"
            borderRadius="20px"
            padding="20px"
            paddingBottom="40px"
            filter="drop-shadow(0 0 10px rgba(0,0,0,.05))"
            textAlign="left"
          >
            <p>C社</p>
            <ParagraphText fontSize="1.1rem" paddingBottom="10px" minH="60px">
              半導体製造装置の真空ポンプ設計開発
            </ParagraphText>
            <dl>
              <Box
                as="dt"
                borderBottom="1px solid #000d1f"
                color="#0a94cf"
                fontWeight="700"
              >
                募集
              </Box>
              <Box as="dd" paddingBottom="10px" minH="60px">
                機械設計、電気回路設計、組み込みソフト
              </Box>
              <Box
                as="dt"
                borderBottom="1px solid #000d1f"
                color="#0a94cf"
                fontWeight="700"
              >
                学べる技術
              </Box>
              <dd>世界トップシェアの技術力</dd>
            </dl>
          </ListItem>
          <ListItem
            w="100%"
            maxW="240px"
            margin="10px"
            bgColor="white"
            borderRadius="20px"
            padding="20px"
            paddingBottom="40px"
            filter="drop-shadow(0 0 10px rgba(0,0,0,.05))"
            textAlign="left"
          >
            <p>D社</p>
            <ParagraphText fontSize="1.1rem" paddingBottom="10px" minH="60px">
              IoT機器・クラウドサービス設計開発
            </ParagraphText>
            <dl>
              <Box
                as="dt"
                borderBottom="1px solid #000d1f"
                color="#0a94cf"
                fontWeight="700"
              >
                募集
              </Box>
              <Box as="dd" paddingBottom="10px" minH="60px">
                機械設計、AIエンジニア
              </Box>
              <Box
                as="dt"
                borderBottom="1px solid #000d1f"
                color="#0a94cf"
                fontWeight="700"
              >
                学べる技術
              </Box>
              <dd>DX化を加速させる最先端技術</dd>
            </dl>
          </ListItem>
        </List>
        <FeatureTopics />
      </Section>

      <Section
        bg="white"
        display="flex"
        flexDirection="column"
        alignItems="center"
        px="20px"
      >
        <Box
          bg="linear-gradient(to bottom, #1064b3, #02b9e4)"
          borderRadius="40px"
          maxW="740px"
          px="5%"
          py="32px"
          marginBottom="100px"
          textAlign="center"
        >
          <ParagraphText color="white" fontSize="2xl">
            今なら登録者全員に
            <br />
            <Box as="span" color="#ffe12e" fontSize="2xl" fontWeight="900">
              理系学部ならではの
              <SmartphoneBr />
              コンテンツを配信中！
            </Box>
          </ParagraphText>
          <List
            display="flex"
            justifyContent="space-between"
            maxW="520px"
            margin="1em auto 0"
          >
            <ListItem w="100%" maxW="130px" paddingRight="10px">
              <Image src={images.checklist} width="120px" alt="" />
              <ParagraphText color="white" fontSize="lg">
                ガクチカ集
              </ParagraphText>
            </ListItem>
            <ListItem w="100%" maxW="130px" paddingRight="10px">
              <Image src={images.questions} width="120px" alt="" />
              <ParagraphText color="white" fontSize="lg">
                面接質問集
              </ParagraphText>
            </ListItem>
            <ListItem w="100%" maxW="130px" paddingRight="10px">
              <Image src={images.more} width="120px" alt="" />
              <ParagraphText color="white" fontSize="lg">
                and more...
              </ParagraphText>
            </ListItem>
          </List>
        </Box>
        <Box px={[4, 0]}>
          <SignInModal {...{ isOpen: open, onClose, defaultAuthState }} />
        </Box>
        <div ref={formRef} />
        <SignUpForm
          showSignInForm={() => {
            setDefaultAuthState(AuthState.SignIn);
            setOpen(true);
          }}
          afterComplete={() => null}
        />
      </Section>
    </>
  );
};

export default Top;
